// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
/**
 * L'affichage d'un examen composé AVEC les versions de ses composants.
 *
 * ── Pourquoi les montrer ensemble ───────────────────────────────────────────
 *
 * Un numéro de version isolé ne renseigne pas. « NFS v3 » ne dit pas si le
 * changement porte sur la liste des paramètres ou sur l'un d'eux ; en regard,
 * « NFS v3 — Hb v2 · GB v1 · … » se lit d'un coup d'œil, et un composant qui
 * vient de bouger saute aux yeux.
 *
 * ── Ce que la ligne PROMET, et qui doit rester vrai ─────────────────────────
 *
 * La version d'un examen monte quand sa composition change ET quand l'un de
 * ses paramètres change (règle du 18/08/2026, `services/versionnage.ts`). Un
 * composant ne peut donc pas être plus récent que l'examen qui le porte : si
 * l'écran en affiche un, ce n'est pas un détail cosmétique — c'est que la
 * propagation n'a pas eu lieu, et le numéro de l'examen ment. `enRetard()`
 * existe pour que ce cas se voie au lieu de passer inaperçu.
 */

export type Composant = { code: string; nom?: string; version?: string | number | null };

/** « v4.2 », ou « — » tant qu'aucune version n'est posée. On n'écrit pas « v0 » :
 *  un examen sans version n'est pas un examen en version zéro.
 *
 *  Décision RQ du 13/09/2026 : la version est désormais « majeur.mineur »
 *  (chaîne, ex. « 4.2 ») — le mineur monte à chaque changement définissant tant
 *  que rien n'a été diffusé, seule la diffusion monte le majeur (cf.
 *  `backend/src/api/ged/services/versionnage.ts`). Un entier hérité d'avant
 *  cette date reste affichable tel quel. */
export const marqueVersion = (v?: string | number | null): string => (v ? `v${v}` : '—');

/** « NFS v3.1 — Hb v2.0 · GB v1.2 · Plaquettes v1.0 » */
export function libelleComposition(
  examen: { code: string; version?: string | number | null }, composants: Composant[],
): string {
  const tete = `${examen.code} ${marqueVersion(examen.version)}`;
  if (!composants.length) return tete;
  return `${tete} — ${composants.map((c) => `${c.code} ${marqueVersion(c.version)}`).join(' · ')}`;
}

/**
 * Les composants dont la version DÉPASSE celle de l'examen.
 *
 * La liste doit rester vide. Elle ne l'est pas si une écriture a contourné le
 * cycle de vie — un script qui pose une version à la main, une reprise
 * interrompue. C'est le seul écart que cet écran signale, parce qu'il rend le
 * numéro affiché faux.
 *
 * La comparaison reste numérique (`Number('4.2') = 4.2`) : elle ne distingue
 * pas le majeur du mineur, mais un composant à 4.2 quand l'examen est à 4.1
 * DOIT se voir, qu'il s'agisse d'un écart de majeur ou de mineur.
 */
export function enRetard(
  examen: { version?: string | number | null }, composants: Composant[],
): Composant[] {
  const v = Number(examen.version) || 0;
  return composants.filter((c) => (Number(c.version) || 0) > v);
}
